import { type IPeerThreadSummary } from '../interface/peer-chat';
import { localThreadId } from './chat-thread-ref';

// The one number on the floating Help button, and where one tap on it should land.
//
// Two sources feed it: the support engine (always mounted) and the peer inbox poll. A peer
// thread whose engine is mounted has a fresher count than the last poll, so the live value
// wins for that thread. Everything here is pure so the routing rule is pinned by a test.

export type ChatUnreadTarget =
	{ kind: 'hub' } | { kind: 'support' } | { kind: 'inbox' } | { kind: 'peer'; summary: IPeerThreadSummary };

export interface IChatUnread {
	support: number;
	/** Sum over every peer thread. */
	peer: number;
	/** The support thread counts as one. */
	threadsWithUnread: number;
	total: number;
	/** What one tap on the FAB should open. */
	target: ChatUnreadTarget;
}

export interface IAggregateUnreadInput {
	supportUnread: number;
	peerThreads: IPeerThreadSummary[];
	/** The reader's id, to turn a thread summary into a local engine id. */
	me: number;
	/** Live engine counts by LOCAL thread id (`peer:{election}:{counterpart}`). */
	liveUnread: ReadonlyMap<string, number>;
	/** Whether the reader supervises others — only a parent has an inbox worth landing on. */
	isParent: boolean;
}

/** The local id the engine for a summary would publish under. Null when the summary is not the reader's. */
export const localIdForSummary = (summary: IPeerThreadSummary, me: number): string | null => {
	const counterpart = summary.counterpart ?? (summary.parent.coreUserId === me ? summary.child : summary.parent);
	if (!counterpart?.coreUserId || !summary.electionId) return null;
	return localThreadId({ kind: 'peer', counterpartCoreUserId: counterpart.coreUserId, electionId: summary.electionId });
};

/** A thread's unread for the reader: the mounted engine's count if there is one, else the poll's. */
export const unreadForSummary = (summary: IPeerThreadSummary, me: number, liveUnread: ReadonlyMap<string, number>): number => {
	const localId = localIdForSummary(summary, me);
	const live = localId ? liveUnread.get(localId) : undefined;
	if (typeof live === 'number') return live;
	return Math.max(0, summary.unreadForMe ?? 0);
};

/**
 * Routing rule, in order:
 *  - nothing unread → the Help hub;
 *  - unread in exactly ONE conversation → that conversation (the badge already said where);
 *  - unread in several → a parent lands on the inbox with "Waiting on you" at the top; a child
 *    has at most two conversations and lands on the hub, where both rows carry badges.
 */
export const aggregateUnread = ({ supportUnread, peerThreads, me, liveUnread, isParent }: IAggregateUnreadInput): IChatUnread => {
	const support = Math.max(0, supportUnread);
	const peerCounts = peerThreads.map((summary) => ({ summary, unread: unreadForSummary(summary, me, liveUnread) }));

	// A hosted engine the inbox has not listed yet (opened from Agent Coverage before the first
	// message, or before the next poll) still counts: its unread is real and on screen.
	const listed = new Set(peerThreads.map((summary) => localIdForSummary(summary, me)));
	let unlistedPeer = 0;
	let unlistedThreads = 0;
	for (const [localId, unread] of liveUnread) {
		if (listed.has(localId) || unread <= 0) continue;
		unlistedPeer += unread;
		unlistedThreads += 1;
	}

	const peer = peerCounts.reduce((sum, p) => sum + p.unread, 0) + unlistedPeer;
	const peerWithUnread = peerCounts.filter((p) => p.unread > 0);
	const threadsWithUnread = (support > 0 ? 1 : 0) + peerWithUnread.length + unlistedThreads;
	const total = support + peer;

	let target: ChatUnreadTarget;
	if (threadsWithUnread === 0) target = { kind: 'hub' };
	else if (threadsWithUnread === 1 && support > 0) target = { kind: 'support' };
	else if (threadsWithUnread === 1 && peerWithUnread.length === 1) target = { kind: 'peer', summary: peerWithUnread[0].summary };
	// One unlisted live thread: there is no summary to open it from, so land where the rows are.
	else target = isParent ? { kind: 'inbox' } : { kind: 'hub' };

	return { support, peer, threadsWithUnread, total, target };
};
